/**
 * Result type for explicit error handling
 *
 * Provides a functional approach to error handling without exceptions.
 * Inspired by Rust's Result type and fp-ts Either.
 *
 * @example
 * // In a use case:
 * async execute(): Promise<Result<Data, AppError>> {
 *   try {
 *     const data = await this.repository.fetch()
 *     return Result.ok(data)
 *   } catch (error) {
 *     return Result.fail(new AppError('FETCH_FAILED', error))
 *   }
 * }
 *
 * // In consumer:
 * const result = await useCase.execute()
 * if (result.isOk()) {
 *   console.log(result.value)
 * } else {
 *   console.error(result.error)
 * }
 */

export type OperationResult<T, E> = Ok<T, E> | Fail<T, E>

export class Ok<T, E> {
  readonly value: T

  constructor(value: T) {
    this.value = value
  }

  isOk(): this is Ok<T, E> {
    return true
  }

  isFail(): this is Fail<T, E> {
    return false
  }

  map<U>(fn: (value: T) => U): OperationResult<U, E> {
    return new Ok(fn(this.value))
  }

  mapError<F>(_fn: (error: E) => F): OperationResult<T, F> {
    return new Ok(this.value)
  }

  getOrElse(_defaultValue: T): T {
    return this.value
  }

  getOrThrow(): T {
    return this.value
  }
}

export class Fail<T, E> {
  readonly error: E

  constructor(error: E) {
    this.error = error
  }

  isOk(): this is Ok<T, E> {
    return false
  }

  isFail(): this is Fail<T, E> {
    return true
  }

  map<U>(_fn: (value: T) => U): OperationResult<U, E> {
    return new Fail(this.error)
  }

  mapError<F>(fn: (error: E) => F): OperationResult<T, F> {
    return new Fail(fn(this.error))
  }

  getOrElse(defaultValue: T): T {
    return defaultValue
  }

  getOrThrow(): T {
    throw this.error
  }
}

export const Result = {
  ok<T, E = never>(value: T): OperationResult<T, E> {
    return new Ok(value)
  },

  fail<T = never, E = unknown>(error: E): OperationResult<T, E> {
    return new Fail(error)
  },

  fromNullable<T, E>(value: T | null | undefined, error: E): OperationResult<T, E> {
    return value != null ? Result.ok(value) : Result.fail(error)
  },

  async fromPromise<T, E = unknown>(promise: Promise<T>, errorMapper: (error: unknown) => E): Promise<OperationResult<T, E>> {
    try {
      const value = await promise
      return Result.ok(value)
    } catch (error) {
      return Result.fail(errorMapper(error))
    }
  },
}
